import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ShoppingBag, 
  CheckCircle, 
  Star, 
  ArrowRight,
  Video,
  FileText,
  Users,
  Calendar,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useMarketplace } from '../hooks/useContent';

const categoryIcons = {
  course: Video,
  digital: FileText,
  coaching: Users,
  workshop: Calendar,
};

const categoryLabels = {
  course: 'Online Course',
  digital: 'Digital Product',
  coaching: 'Coaching',
  workshop: 'Workshop',
};

export default function Marketplace() {
  // Fetch marketplace items from Contentful (with static fallback)
  const { items: marketplaceData, isLoading, error } = useMarketplace();

  const featuredItems = marketplaceData.filter(item => item.featured);
  const otherItems = marketplaceData.filter(item => !item.featured);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
        <span className="ml-2 text-slate-600">Loading marketplace...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Failed to load marketplace: {error}</p>
          <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="marketplace-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Marketplace
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              Resources for Your Growth
            </h1>
            <p className="text-slate-600 text-lg">
              Courses, templates, coaching sessions, and workshops designed to help you become
              a more effective leader. Invest in yourself.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Items */}
      {featuredItems.length > 0 && (
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-brand-secondary font-semibold tracking-widest uppercase text-sm">
                Featured
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mt-2">
                Most Popular
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {featuredItems.map((item, index) => {
                const CategoryIcon = categoryIcons[item.category] || ShoppingBag;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group"
                  >
                    <div className="bg-white border-2 border-brand-secondary/20 rounded-3xl overflow-hidden hover:border-brand-secondary hover:shadow-2xl transition-all duration-500">
                      {/* Image */}
                      <div className="relative aspect-[16/9] overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="inline-flex items-center gap-2 bg-white/95 backdrop-blur text-brand-primary text-xs font-semibold px-3 py-1.5 rounded-full">
                            <CategoryIcon className="w-4 h-4 text-brand-secondary" />
                            {categoryLabels[item.category]}
                          </span>
                        </div>
                        {item.originalPrice && (
                          <div className="absolute top-4 right-4">
                            <span className="bg-brand-secondary text-white text-xs font-bold px-3 py-1.5 rounded-full">
                              SAVE ${item.originalPrice - item.price}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6 md:p-8">
                        <h3 className="font-heading text-2xl font-bold text-brand-primary mb-2">
                          {item.name}
                        </h3>
                        <p className="text-slate-600 mb-6">
                          {item.description}
                        </p>

                        {/* Features */}
                        <ul className="space-y-2 mb-6">
                          {item.features.slice(0, 4).map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <CheckCircle className="w-4 h-4 text-brand-secondary flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>

                        {/* Price & CTA */}
                        <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                          <div>
                            <div className="flex items-baseline gap-2">
                              <span className="font-heading text-3xl font-bold text-brand-primary">
                                ${item.price}
                              </span>
                              {item.originalPrice && (
                                <span className="text-lg text-slate-400 line-through">
                                  ${item.originalPrice}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold px-6"
                            data-testid={`buy-${item.id}`}
                          >
                            <ShoppingBag className="w-4 h-4 mr-2" />
                            Get Access
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Other Items */}
      {otherItems.length > 0 && (
        <section className="py-20 md:py-32 bg-brand-accent">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary">
                More Resources
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {otherItems.map((item, index) => {
                const CategoryIcon = categoryIcons[item.category] || ShoppingBag;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 flex gap-6 hover:shadow-xl transition-shadow"
                  >
                    {/* Image */}
                    <div className="flex-shrink-0 w-32 h-32 rounded-xl overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-secondary mb-2">
                        <CategoryIcon className="w-3 h-3" />
                        {categoryLabels[item.category]}
                      </span>
                      <h3 className="font-heading text-lg font-bold text-brand-primary mb-1">
                        {item.name}
                      </h3>
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                        {item.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="font-heading text-xl font-bold text-brand-primary">
                          ${item.price}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-sm"
                        >
                          Learn More
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* WooCommerce Note */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 text-center">
            <p className="text-slate-600 text-sm">
              <strong>WordPress Note:</strong> This marketplace can be powered by WooCommerce for full
              e-commerce functionality including payment processing, digital downloads, and order management.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-brand-primary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-6">
              Need Custom Solutions?
            </h2>
            <p className="text-white/70 text-lg mb-8">
              For enterprise training, custom workshops, or tailored consulting packages,
              let's discuss your specific needs.
            </p>
            <Button
              asChild
              size="lg"
              className="rounded-full bg-brand-secondary text-white hover:bg-brand-secondary/90 font-semibold px-8"
            >
              <Link to="/contact">
                Get In Touch <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
